import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTenantApi } from '@/hooks';
import { CharacteristicDimension } from '@/types/ReportRun';
import { DefaultRunConfig } from '@/types/ApiTypes';

export interface FilterState {
    categories: string[];
    parentCompanies: string[];
    manufacturers: string[];
    brands: string[];
    upcs: string[];
    productLevel: string[];
    productDimensions: CharacteristicDimension[];
}

export interface FilterOptions {
    parentCompanies: Array<{ display: string; value: string }>;
    manufacturers: Array<{ display: string; value: string }>;
    brands: Array<{ display: string; value: string }>;
}

/**
 * Compute default filter state from DefaultRunConfig.
 * Extracted to allow recalculation when config changes.
 */
const getDefaultFilterState = (defaultRunConfig?: DefaultRunConfig): FilterState => {
    const defaultCategories = defaultRunConfig?.rms?.categories || [];
    const defaultProductDimensions = defaultCategories.length > 0
            ? [{ characteristic: 'Form', characteristicsCode: 'bc_form_total_store', isCustom: false }]
            : [];

    return {
        categories: defaultCategories,
        parentCompanies: [],
        manufacturers: [],
        brands: [],
        upcs: [],
        productLevel: ['Category'],
        productDimensions: defaultProductDimensions,
    };
};

export const useItemCodingExplorerFilters = (defaultRunConfig?: DefaultRunConfig) => {
    const {
        getParentCompanySearch,
        getManufacturerSearch,
        getBrandsSearch,
    } = useTenantApi();

    // Memoize default state to avoid unnecessary recalculations
    const defaultFilterState = useMemo(() => getDefaultFilterState(defaultRunConfig), [defaultRunConfig]);

    const [filters, setFilters] = useState<FilterState>(defaultFilterState);
    const [appliedFilters, setAppliedFilters] = useState<FilterState>(defaultFilterState);

    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        parentCompanies: [],
        manufacturers: [],
        brands: [],
    });
    const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
    const MAX_SEARCH_RESULTS = 10000;

    // Sync filter state when defaultRunConfig changes
    // This ensures that if the config is loaded/updated after mount, filters pick up the new defaults
    useEffect(() => {
        setFilters(defaultFilterState);
        setAppliedFilters(defaultFilterState);
    }, [defaultFilterState]);

    const handleFilterChange = useCallback((e: any) => {
        const { name, value } = e;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };

    const handleSort = (a: any, b: any) => a.display.localeCompare(b.display);

    // Debounced, batched fetch — waits 500ms after the last category change, then fires
    // all 3 API calls together and applies results in a single setFilterOptions call.
    const categoryFetchTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const categoryFetchVersionRef = useRef(0);

    useEffect(() => {
        // Bump version to invalidate any in-flight requests from a previous render
        const currentVersion = ++categoryFetchVersionRef.current;

        // Clear any pending debounce timer from the previous render
        if (categoryFetchTimerRef.current) {
            clearTimeout(categoryFetchTimerRef.current);
        }

        if (!filters.categories || filters.categories.length === 0) {
            setFilterOptionsLoading(false);
            setFilterOptions((prev) => ({
                ...prev,
                parentCompanies: [],
                manufacturers: [],
                brands: [],
            }));
            return;
        }

        setFilterOptionsLoading(true);

        categoryFetchTimerRef.current = setTimeout(async () => {
            try {
                const [parentCompanyRes, manufacturerRes, brandsRes] = await Promise.all([
                    getParentCompanySearch({ categories: filters.categories, maxResults: MAX_SEARCH_RESULTS }),
                    getManufacturerSearch({ categories: filters.categories, maxResults: MAX_SEARCH_RESULTS }),
                    getBrandsSearch({ categories: filters.categories, maxResults: MAX_SEARCH_RESULTS }),
                ]);

                // Discard if categories changed while requests were in-flight
                if (currentVersion !== categoryFetchVersionRef.current) return;

                const formattedCompanies =
                    parentCompanyRes?.results?.map((company: any) => ({
                        display: company.parentCompany || company.name || company,
                        value: company.parentCompany || company.name || company,
                    })) || [];

                const formattedManufacturers =
                    manufacturerRes?.results?.map((manufacturer: any) => ({
                        display: manufacturer.manufacturer || manufacturer.name || manufacturer,
                        value: manufacturer.manufacturer || manufacturer.name || manufacturer,
                    })) || [];

                const formattedBrands =
                    brandsRes?.results?.map((brand: any) => ({
                        display: brand.brand || brand.name || brand,
                        value: brand.brand || brand.name || brand,
                    })) || [];

                // Single state update — one re-render instead of three
                setFilterOptions((prev) => ({
                    ...prev,
                    parentCompanies: formattedCompanies.sort(handleSort),
                    manufacturers: formattedManufacturers.sort(handleSort),
                    brands: formattedBrands.sort(handleSort),
                }));
                setFilterOptionsLoading(false);
            } catch (error) {
                if (currentVersion !== categoryFetchVersionRef.current) return;
                console.error('Error fetching filter options:', error);
                setFilterOptions((prev) => ({
                    ...prev,
                    parentCompanies: [],
                    manufacturers: [],
                    brands: [],
                }));
                setFilterOptionsLoading(false);
            }
        }, 500);

        return () => {
            if (categoryFetchTimerRef.current) {
                clearTimeout(categoryFetchTimerRef.current);
            }
        };
    }, [filters.categories]);

    return {
        filters,
        appliedFilters,
        filterOptions,
        filterOptionsLoading,
        handleFilterChange,
        handleApplyFilters,
    };
};
